import { curry } from "lodash";

import {
    AllResultsSelectionItem,
    getItemWithTheSmallestValueOrManual,
    type SelectionDropdown,
    type SelectionDropdownItem,
    TopNRemainingSelectionItem,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import type { LeadsType } from "app/pages/sales-intelligence/sub-modules/company-search/types/common";

import { topNItemMap } from "../../../configuration/tableActions";

type ValuesParams = {
    topNValues: readonly number[];
    fixedLimit?: number;
};

function createExportSelectionDropdown(
    { topNValues, fixedLimit = Number.POSITIVE_INFINITY }: ValuesParams,
    leadsType: LeadsType,
): SelectionDropdown {
    return {
        getInitiallySelectedItem: getItemWithTheSmallestValueOrManual,
        getItems<Item extends SelectionDropdownItem>(
            numberOfResults: number,
            limit = fixedLimit,
            mapLimitedListOfItems?: (listOfItems: readonly number[]) => readonly Item[],
        ): readonly Item[] | readonly SelectionDropdownItem[] {
            if (mapLimitedListOfItems) {
                return mapLimitedListOfItems(topNValues);
            }

            const lowestLimit = Math.min(limit, fixedLimit);
            const maximumPossibleValue = Math.min(numberOfResults, lowestLimit);

            if (maximumPossibleValue <= 0) {
                return [];
            }

            const TopNSelectionItem = topNItemMap[leadsType];
            const possibleTopNValues = topNValues.filter((value) => value < maximumPossibleValue);
            const topNItems: SelectionDropdownItem[] = possibleTopNValues.map((value) => {
                return new TopNSelectionItem({ value, id: `top-${value}` });
            });

            if (lowestLimit <= numberOfResults) {
                return topNItems.concat(
                    new TopNRemainingSelectionItem({
                        id: `top-${lowestLimit}`,
                        value: lowestLimit,
                    }),
                );
            }

            return topNItems.concat(new AllResultsSelectionItem(numberOfResults));
        },
    };
}

export default curry(createExportSelectionDropdown);
